import React from 'react';
import SiteLayout, { PALETTE } from './SiteLayout';
import SEO from '../../components/SEO';

const SITE_URL = 'https://keystone.cloudvoro.com';

const SECTION_TITLE = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: 'clamp(22px, 2.6vw, 30px)',
  fontWeight: 700,
  color: PALETTE.navy,
  margin: '40px 0 14px',
  letterSpacing: '-0.015em',
};

const BODY = { fontSize: 15.5, lineHeight: 1.7, color: PALETTE.slate, margin: '0 0 14px' };

const Pill = ({ status, children }) => {
  const colors = {
    live:     { bg: '#ECFDF5', fg: '#047857', label: 'LIVE' },
    progress: { bg: '#FFFBEB', fg: '#B45309', label: 'IN PROGRESS' },
    planned:  { bg: '#EFF6FF', fg: '#1D4ED8', label: 'PLANNED' },
    aligned:  { bg: '#EEF2FF', fg: '#3730A3', label: 'ALIGNED' },
  };
  const c = colors[status] || colors.live;
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 9px',
      borderRadius: 3,
      background: c.bg,
      color: c.fg,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: '0.12em',
      verticalAlign: 'middle',
      marginRight: 10,
    }}>{c.label}</span>
  );
};

export default function SiteSecurity() {
  return (
    <SiteLayout>
      <SEO
        title="Security & Compliance, Keystone by CloudVoro"
        description="Enterprise-grade security as standard: EU-hosted (AWS Ireland) or on-premise, tenant isolation, AES-256 at rest, TLS 1.3 in transit, MFA for every user, ISO 27001-aligned controls."
        path="/site/security"
        keywords="Keystone security, CloudVoro security posture, food software security Ireland, ISO 27001 aligned food software, AWS Ireland hosting, on-premise food software, tenant isolation, MFA food manufacturing software, encryption at rest TLS 1.3"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Security & Compliance',
          url: `${SITE_URL}/site/security`,
          description:
            'Plain-language security posture for Keystone by CloudVoro, hosting, encryption, access controls and compliance.',
        }}
      />

      <section style={{ background: PALETTE.cream, padding: '80px 32px 24px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }} data-testid="security-posture">
          <div style={{
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            fontWeight: 700, color: PALETTE.magenta, marginBottom: 18,
          }}>Security & compliance</div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(36px, 5vw, 56px)',
            fontWeight: 700,
            color: PALETTE.navy,
            margin: 0,
            letterSpacing: '-0.025em',
            lineHeight: 1.05,
          }}>
            Security built in.<br/>
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>Not bolted on.</em>
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.6, color: PALETTE.slate, marginTop: 22, maxWidth: 720 }}>
            Plain-language security posture for Keystone. Every control we run, encryption, tenant isolation, MFA, audit trails, bot protection, listed in one place so procurement teams don't have to guess.
          </p>
          <div style={{ fontSize: 12, color: PALETTE.muted, marginTop: 18, letterSpacing: '0.04em' }}>
            Effective May 2026 · Last reviewed May 2026
          </div>
        </div>
      </section>

      <section style={{ background: PALETTE.paper, padding: '32px 32px 96px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }}>

          <h2 style={SECTION_TITLE}>1. Hosting & data residency</h2>
          <p style={BODY}>
            <Pill status="live" />
            Production data is hosted in <strong style={{ color: PALETTE.navy }}>AWS Ireland (eu-west-1, Dublin)</strong>. No customer data leaves the EU without prior written consent (and we've never asked).
          </p>
          <p style={BODY}>
            Each Keystone tenant runs in a logically isolated MongoDB database under a single regional cluster. Cross-tenant queries are impossible at the application layer (every query carries a `tenant_id` predicate enforced server-side; see audit-log section below).
          </p>
          <p style={BODY}>
            <Pill status="live" />
            <strong style={{ color: PALETTE.navy }}>Cloud or on-premise, your call.</strong> Keystone deploys on AWS EU infrastructure as standard, or as a local on-premise installation on your own servers where company policy, retailer contracts or data-sovereignty requirements demand it. Same product, same audit trail, your infrastructure.
          </p>

          <h2 style={SECTION_TITLE}>2. Encryption</h2>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li><Pill status="live" /><strong style={{ color: PALETTE.navy }}>At rest:</strong> AES-256 via AWS KMS-managed keys. Backups encrypted with the same key hierarchy.</li>
            <li><Pill status="live" /><strong style={{ color: PALETTE.navy }}>In transit:</strong> TLS 1.3 enforced on all public endpoints. HSTS preload-eligible. No TLS 1.0 / 1.1 fallback.</li>
            <li><Pill status="live" /><strong style={{ color: PALETTE.navy }}>Field-level:</strong> Sensitive PII fields (customer delivery addresses, contact phones, supplier banking metadata) are encrypted application-side before reaching MongoDB.</li>
          </ul>

          <h2 style={SECTION_TITLE}>3. Access controls</h2>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li><Pill status="live" />Per-tenant role-based access with least-privilege defaults (super_admin · admin · ops_user · viewer · demo).</li>
            <li><Pill status="live" />Industry-standard salted password hashing with timing-safe verification.</li>
            <li><Pill status="live" /><strong style={{ color: PALETTE.navy }}>Multi-factor authentication for every user</strong>, TOTP-based MFA available across all roles, not just admins.</li>
            <li><Pill status="live" />Signed, short-lived session tokens with idle expiry and per-deployment secrets.</li>
            <li><Pill status="live" />API rate limiting on all public and authenticated endpoints.</li>
            <li><Pill status="live" />Failed-login throttling and brute-force IP lockout on all authentication endpoints.</li>
            <li><Pill status="live" />reCAPTCHA bot protection on public-facing forms, signups, contact and login flows are shielded from automated abuse.</li>
          </ul>

          <h2 style={SECTION_TITLE}>4. Audit trail</h2>
          <p style={BODY}>
            <Pill status="live" />
            Every state-changing API call is recorded into an immutable audit log: actor, tenant, action, resource, before/after delta, timestamp, source IP. The log is queryable from the Admin Console and exportable as signed PDF, this is what BRCGS traceability requirements evidence looks like in practice. Status transitions on Traceability Events maintain their own status-history sub-log on top.
          </p>

          <h2 style={SECTION_TITLE}>5. Compliance & standards</h2>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li><Pill status="live" /><strong style={{ color: PALETTE.navy }}>GDPR</strong> (Regulation (EU) 2016/679), controller for site visitors, processor for tenant data. See <a href="/site/privacy" style={{ color: PALETTE.magenta, fontWeight: 600 }}>Privacy Policy</a>.</li>
            <li><Pill status="live" /><strong style={{ color: PALETTE.navy }}>Irish Data Protection Act 2018</strong>, domestic implementation of GDPR.</li>
            <li><Pill status="live" /><strong style={{ color: PALETTE.navy }}>EU food safety regulations</strong> traceability requirements, Keystone's batch model is designed to evidence Art. 18 obligations end-to-end.</li>
            <li><Pill status="aligned" /><strong style={{ color: PALETTE.navy }}>ISO/IEC 27001:2022</strong>, our security controls are mapped to the Annex A control framework: access management, encryption, logging, incident response and vendor risk all follow the standard's structure.</li>
            <li><Pill status="aligned" /><strong style={{ color: PALETTE.navy }}>NIS2 (EU Directive 2022/2555)</strong>, customers in NIS2 scope (essential or important entities) can rely on Keystone's tenant isolation, MFA and immutable audit-trail controls as direct supporting evidence for their own obligations.</li>
          </ul>

          <h2 style={SECTION_TITLE}>6. Data protection lead & ADPO membership</h2>
          <p style={BODY}>
            <Pill status="live" />
            CloudVoro operates with a <strong style={{ color: PALETTE.navy }}>Data Protection Lead (Privacy Contact)</strong> who handles subject access requests and supervisory-authority correspondence, reachable at{' '}
            <a href="mailto:privacy@cloudvoro.com" style={{ color: PALETTE.magenta, fontWeight: 600 }}>privacy@cloudvoro.com</a>.
          </p>
          <p style={BODY}>
            Our founder Asif Khan is a member of the <strong style={{ color: PALETTE.navy }}>Association of Data Protection Officers (ADPO) Ireland</strong>, and CloudVoro reviews its data-protection governance on an ongoing basis to keep pace with customer contracts and regulatory expectations.
          </p>

          <h2 style={SECTION_TITLE}>7. Sub-processors & vendor risk</h2>
          <p style={BODY}>
            Limited, named sub-processors with EU-or-equivalent data-protection guarantees. The current list, contact addresses and processing purposes are kept on a dedicated page,{' '}
            <a href="/legal/sub-processors" style={{ color: PALETTE.magenta, fontWeight: 600 }}>/legal/sub-processors</a>. Material changes are emailed to the primary tenant contact 30 days before they take effect, in line with Art. 28(2) GDPR.
          </p>

          <h2 style={SECTION_TITLE}>8. Vulnerability disclosure</h2>
          <p style={BODY}>
            If you believe you've found a security vulnerability in Keystone, please write to{' '}
            <a href="mailto:security@cloudvoro.com" style={{ color: PALETTE.magenta, fontWeight: 600 }}>security@cloudvoro.com</a>{' '}
            with steps to reproduce. We acknowledge within 2 business days and aim to provide a fix or mitigation timeline within 10 business days. We credit researchers publicly with permission and provide a written acknowledgement letter.
          </p>

          <h2 style={SECTION_TITLE}>9. Backup & disaster recovery</h2>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li><Pill status="live" />Encrypted MongoDB Atlas continuous backups, 30-day point-in-time recovery window.</li>
            <li><Pill status="live" />Recovery Time Objective (RTO): 4 hours for full-region failure. Recovery Point Objective (RPO): &lt; 1 minute under normal operation, ≤ 15 minutes worst case.</li>
          </ul>

          <h2 style={SECTION_TITLE}>10. Incident response</h2>
          <p style={BODY}>
            We notify the primary tenant contact within <strong style={{ color: PALETTE.navy }}>72 hours</strong> of confirming a personal-data breach, in line with Art. 33 GDPR. If you're a customer, the named contact for your tenant is recorded in your Master Services Agreement. Generic security notices land in your in-product Admin Console.
          </p>

          <h2 style={SECTION_TITLE}>11. Customer responsibilities</h2>
          <p style={BODY}>
            Security is a shared model. The Customer is responsible for: choosing strong passwords / enabling MFA on super_admin accounts, granting least-privilege access via the Roles screen, keeping the tenant's contact list current, reviewing the audit log on a cadence appropriate to their certification scheme, and notifying us promptly of staff offboarding.
          </p>

          <div style={{
            marginTop: 40,
            padding: '20px 24px',
            background: PALETTE.cream,
            border: `1px solid ${PALETTE.border}`,
          }}>
            <Subhead>Procurement enquiries</Subhead>
            <p style={{ ...BODY, margin: 0 }}>
              Need a completed security questionnaire (SIG Lite / CAIQ / a retailer's bespoke form)? Email{' '}
              <a href="mailto:security@cloudvoro.com" style={{ color: PALETTE.magenta, fontWeight: 600 }}>security@cloudvoro.com</a>{' '}
             , typical turnaround 3 business days for a Lite questionnaire, 5–7 for a full one.
            </p>
          </div>

        </div>
      </section>
    </SiteLayout>
  );
}

const Subhead = ({ children }) => (
  <h3 style={{
    fontSize: 13,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: PALETTE.magenta,
    margin: '22px 0 6px',
  }}>{children}</h3>
);
